import React, { forwardRef, useEffect, useState } from "react";
import axios from "axios";
import { Auth } from "aws-amplify";
import { Button, CircularProgress, Dialog, IconButton, Menu, MenuItem, Slide, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

import InfoIcon from '@mui/icons-material/Info';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import StudentReport from "./views/Facilitator/StudentReport";
import StudentReportDemo from "./views/Demo/StudentReport";
import TransferStudent from "./views/Admin/TransferStudent";
import DeleteStudent from "./views/Admin/DeleteStudent";
import EditStudent from "./views/Admin/EditStudent";

function descendingComparator(a, b, orderByValue) {
  if (b[orderByValue] < a[orderByValue]) {
    return -1;
  }
  if (b[orderByValue] > a[orderByValue]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderByValue) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderByValue)
    : (a, b) => -descendingComparator(a, b, orderByValue)
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// Dialogue
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const TableComponent = ({columns, orderBy, tableData, tableHeight, sort, disableSrNo, loading, sortBy, maxRowsPerPage}) => {

  useEffect(() => {
    // currently used in expert student rating so that it doesn't reset to 0 when opening report since this useEffect is ran whenever data changes
    if(maxRowsPerPage) setPage(prev => (prev > (tableData.length/maxRowsPerPage)) ? 0 : prev)
    else setPage(0)
    setOrderByValue(sortBy ? sortBy : orderBy[0])
  }, [tableData, orderBy, sortBy, maxRowsPerPage])

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(maxRowsPerPage ? maxRowsPerPage : 30);
  const [order, setOrder] = useState(sort ? sort : 'asc');
  const [orderByValue, setOrderByValue] = useState(sortBy ? sortBy : orderBy[0]);

  const { t } = useTranslation();

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(+e.target.value);
    setPage(0);
  };

  const handleRequestSort = (header) => {
    setOrder(orderByValue === header && order === 'asc' ? 'desc' : 'asc');
    setOrderByValue(header);
  };

  // download
  const [downloading, setDownloading] = useState(false)

  const handleDownload = (data) => {
    if(!data) return
    if(data["pdfUrl"] !== undefined){
      let pdfUrl = data.pdfUrl
      // console.log(pdfUrl)
      setDownloading(pdfUrl)

      Auth.currentSession()
      .then((data) => {
        const jwtToken = data.idToken.jwtToken;
        const facId = data.idToken.payload["custom:db_id"]

        const getReportData = async(facId, pdfUrl) => {
          try {
            const response =  await axios.get("https://zk1128nq2f.execute-api.ap-south-1.amazonaws.com/alpha/facilitator/"+facId+"/student-report-download?pdfUrl="+pdfUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': jwtToken
                }
            });
            // console.log(response.data)
            const link = document.createElement('a');
            link.download = "report.pdf";
            link.href = response.data.pdfUrl;
            link.target = "_blank"
            link.click();
            setDownloading(false)
          }
          catch (error) {
            console.log(error)
            if(error.response.status === 404) alert("Report does not exist!")
            else alert("Internal Server Error - Please contact IITB Team")
            setDownloading(false)
          }
        }
        getReportData(facId, pdfUrl)
      })
      .catch((err) => console.log(err));
    }
    // admin class pdf zip
    if(data["pdfZip"] !== undefined){
      // console.log(data)
      setDownloading(data.pdfZip)
      let testId = data.testId
      let schoolId = data.schoolId
      let std = data.std
      let divn = data.divn

      Auth.currentSession()
      .then((data) => {
        const jwtToken = data.idToken.jwtToken;
        const adminId = data.idToken.payload["custom:db_id"]

        const getReportData = async(adminId, testId, schoolId, std, divn) => {
          try {
            const response =  await axios.get("https://zk1128nq2f.execute-api.ap-south-1.amazonaws.com/alpha/admin/"+adminId+"/class-report-download?testId="+testId+"&schoolId="+schoolId+"&std="+std+"&divn="+divn, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': jwtToken
                }
            });
            // console.log(response.data)
            const link = document.createElement('a');
            link.download = String(std) + "_" + divn + "_report.zip";
            link.href = response.data.pdfZip;
            link.target = "_blank"
            link.click();
            setDownloading(false)
          }
          catch (error) {
            console.log(error)
            if(error.response.status === 404) alert("Report does not exist!")
            else if(error.response.status === 406) alert(error.response.data)
            else alert("Internal Server Error - Please contact IITB Team")
            setDownloading(false)
          }
        }
        getReportData(adminId, testId, schoolId, std, divn)
      })
      .catch((err) => console.log(err));
    }
  }

  // Dialogue
  const [open, setOpen] = useState(false);
  const [dialogueData, setDialogueData] = useState("")
  const maxWidth = "lg"

  const handleClickOpen = (data) => {
    setOpen(true);
    setDialogueData(data)
  };

  const handleClose = () => {
    setOpen(false);
    // setDialogueData("")       // comment this if you want to keep the loaded data after closing the dialogue
    if(dialogueData.testId !== undefined) setDialogueData("")     // only clears it for student report
  };

  // options
  const [optionsData, setOptionsData] = useState("")
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openOptions = Boolean(anchorEl);
  const handleClick = (event, optionsData) => {
    setAnchorEl(event.currentTarget);
    setOptionsData(optionsData)
  };
  const handleCloseOptions = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <TableContainer {...tableHeight ? {sx: { maxHeight: tableHeight }} : {sx: { maxHeight: "57vh"}}}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {!disableSrNo && 
                <TableCell
                  key="srNo"
                  align="center"
                  style={{ minWidth: 20, padding: "12px" }}
                >
                  <h4>{t("commonComponents.table.tableHeader_srNo")}</h4>
                </TableCell>
              }
              {columns.map((column) =>
                orderBy.includes(column.key) ? (
                  <TableCell
                    key={column.key}
                    align="center"
                    sortDirection={orderByValue === column.key ? order : false}
                    style={{ minWidth: column.width, padding: "12px" }}
                  >
                    <TableSortLabel
                      active={orderByValue === column.key}
                      direction={orderByValue === column.key ? order : 'asc'}
                      sx={{"& svg": {"opacity": 0.25}}}
                      onClick={() => handleRequestSort(column.key)}
                    >
                      <h4>{column.headerName}</h4>
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell
                    key={column.key}
                    align="center"
                    style={{ minWidth: column.width, padding: "12px" }}
                  >
                    <h4>{column.headerName}</h4>
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? 
              <TableRow hover tabIndex={-1}>
                <TableCell colSpan={columns.length + 1} style={{textAlign: "center", padding: "12px"}}>
                   <CircularProgress color="info" size={25}/>
                </TableCell>
              </TableRow>
            :
              <>
                {tableData.length > 0 ? (
                  stableSort(tableData, getComparator(order, orderByValue))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, i) => {
                      return (
                        <TableRow hover tabIndex={-1} key={i} style={columns[0].color ? { backgroundColor: row[columns[0].color]} : {}}>
                          {!disableSrNo &&
                            <TableCell
                              style={{textAlign: "center", padding: "12px"}}
                              // style={columns[0].color ? {textAlign: "center", padding: "12px", borderColor: "rgba(0, 0, 0, 0.23)"} : 
                              //         {textAlign: "center", padding: "12px"}}
                            >
                              {i + 1 + page * rowsPerPage}
                            </TableCell>
                          }
                          {columns.map((column) =>
                            <TableCell
                              style={{textAlign: "center", padding: "12px"}}
                              // style={columns[0].color ? {textAlign: "center", padding: "12px", borderColor: "rgba(0, 0, 0, 0.23)"} : 
                              //         {textAlign: "center", padding: "12px"}}
                              key={column.key}
                            >
                              {(!column.date && !column.percent && !column.tooltip && !column.download && !column.readerCat && !column.previousCategory && !column.dialogue && !column.options && !column.button) && row[column.key]}
                              {column.date && (row[column.key] ? new Date(row[column.key]).toLocaleDateString("es-CL") : row[column.key])}
                              {(column.percent && !column.tooltip) && row[column.key] + "%"}
                              {column.tooltip && ((row[column.key] === -1 || row[column.key] === -2 || row[column.key] === -0.5) ? 
                                <>
                                  {row[column.key] === -1 ? "Invalid Attempt" : row[column.key] === -2 ? "Audio Missing" : "Not Qualified"} 
                                    <Tooltip 
                                      enterTouchDelay={50} 
                                      arrow 
                                      title={row[column.key] === -1 ? 
                                              "WCPM cannot be calculated since student did not read atleast half of all paragraphs in the story or Audio was blank." : 
                                            row[column.key] === -0.5 ?
                                              "Phrasing score is only computed for WCPM>=85 & Reading Accuracy>=90%" :
                                              "WCPM cannot be calculated as test audios were deleted before uploading attempt."
                                            }
                                    >
                                      <IconButton sx={{padding: 0, color: "#0288D1"}}><InfoIcon/></IconButton>
                                    </Tooltip> 
                                </> 
                                : row[column.key] === -4 ? "Processing" 
                                : row[column.key] === -3 ? "Not Tested/Uploaded" 
                                : column.readerCat ? row[column.readerCat] 
                                : column.percent ? row[column.key] + "%"
                                : row[column.key] 
                                )
                              }
                              {column.download &&
                                <>
                                  {(downloading && row[column.key]===downloading) ?
                                    <CircularProgress color="info" size={16} style={{placeSelf: "center"}}/>  
                                  :
                                    <IconButton
                                      size="small"
                                      aria-label="download"
                                      sx={{padding: 0}}
                                      color="info"
                                      onClick={() => handleDownload(
                                        column.key==="pdfUrl" ? {
                                          "pdfUrl": row["pdfUrl"],
                                        }
                                        : column.key==="pdfZip" ? {
                                          "pdfZip": row["pdfZip"],
                                          "testId": column.testId,
                                          "schoolId": row["schoolId"],
                                          "std": row["std"],
                                          "divn": row["divn"]
                                        }
                                        : false
                                      )}
                                      disabled={row[column.key] ? downloading ? true : false : true}
                                    >
                                      <FileDownloadIcon />
                                    </IconButton>
                                  }
                                </>
                              }
                              {column.previousCategory && 
                                <>
                                  {/* {row[column.key]} */}
                                  {row[column.previousCategory] && 
                                    (row[column.previousCategory].includes("Unchanged") ?
                                      <Tooltip 
                                        enterTouchDelay={50} 
                                        arrow 
                                        title={<span className="fontFamily-Montserrat">{row[column.previousCategory]}</span>}>
                                          <IconButton sx={{padding: 0, color: "unset"}}><PlayArrowIcon sx={{rotate: "180deg"}} fontSize="small" viewBox="6 0 24 24"/><PlayArrowIcon fontSize="small" viewBox="6 0 24 24"/></IconButton>
                                      </Tooltip> 
                                    : row[column.previousCategory].includes("Improved") ?
                                      <Tooltip 
                                        enterTouchDelay={50} 
                                        arrow 
                                        title={<span className="fontFamily-Montserrat">{row[column.previousCategory]}</span>}>
                                          <IconButton sx={{padding: 0, color: "green"}}><PlayArrowIcon sx={{rotate: "-90deg"}} fontSize="small"/></IconButton>
                                      </Tooltip>
                                    :
                                      <Tooltip 
                                        enterTouchDelay={50} 
                                        arrow 
                                        title={<span className="fontFamily-Montserrat">{row[column.previousCategory]}</span>}>
                                          <IconButton sx={{padding: 0, color: "red"}}><PlayArrowIcon sx={{rotate: "90deg"}} fontSize="small"/></IconButton>
                                      </Tooltip>                                 
                                    )
                                  }
                                </>
                              }
                              {(column.dialogue && (column.dialogue === "report" || column.dialogue === "demo-report")) &&
                                // <StudentReport testId={column.testId} studId={row[column.key]}/>
                                <Button 
                                  size="small" 
                                  disabled={!row[column.key]}
                                  onClick={() => handleClickOpen({"dialogue": column.dialogue, "testId": column.testId, "studId": row["studId"], "lang": column.lang, "allLangs": column.allLangs, "thresholds": column.thresholds})} 
                                  variant="contained" 
                                  startIcon={<AssessmentIcon />} 
                                >
                                  {t("commonComponents.table.buttonLabel_viewReport")}
                                </Button>
                              }
                              {column.options &&
                                <IconButton
                                  size="small"
                                  aria-label="more"
                                  id="long-button"
                                  aria-controls={openOptions ? 'options-menu' : undefined}
                                  aria-expanded={openOptions ? 'true' : undefined}
                                  aria-haspopup="true"
                                  sx={{padding: 0}}
                                  onClick={(e) => handleClick(e, 
                                    column.options==="students" ? {
                                        data: {...row, "test": column.test}, 
                                        // options: ["Edit Details", "Delete Student", "Mark Long Absentee", "Transfer to Dropbox"],
                                        // icons: [<EditIcon sx={{mr: 1, fontSize: "inherit"}}/>,<DeleteIcon sx={{mr: 1, fontSize: "inherit"}}/>,<ArchiveIcon sx={{mr: 1, fontSize: "inherit"}}/>,<ArchiveIcon sx={{mr: 1, fontSize: "inherit"}}/>]
                                        options: ["Edit Details", "Delete Student", "Mark Long Absentee"],
                                        icons: [<EditIcon sx={{mr: 1, fontSize: "inherit"}}/>,<DeleteIcon sx={{mr: 1, fontSize: "inherit"}}/>,<ArchiveIcon sx={{mr: 1, fontSize: "inherit"}}/>]
                                      }
                                    : column.options==="orgs" && {
                                        data: {...row, "handleClick": column.handleClick}, 
                                        options: ["Edit Admin and Class", "Edit Stories"],
                                        icons: [<EditIcon sx={{mr: 1, fontSize: "inherit"}}/>,<EditIcon sx={{mr: 1, fontSize: "inherit"}}/>]
                                      }
                                  )}
                                  disabled={column.options==="students" && row["rollNo"]===0}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              }
                              {(column.button) &&
                                <Button 
                                  size={column.button.size ? column.button.size : "small"} 
                                  disabled={column.button.disabled}
                                  onClick={() => column.button.handleClick(row)} 
                                  variant="contained" 
                                  {...column.button.style && {sx: column.button.style}}
                                  // startIcon={<AssessmentIcon />} 
                                >
                                  {column.button.name}
                                </Button>
                              }
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                ) : (
                  <TableRow hover tabIndex={-1}>
                    <TableCell colSpan={columns.length + 1} style={{textAlign: "center", padding: "12px"}}>
                      {t("commonComponents.table.noData")}
                    </TableCell>
                  </TableRow>
                )}
              </>
            }
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        labelRowsPerPage={t("commonComponents.table.tablePaginiation_rowsPerPageLabel")}
        rowsPerPageOptions={[10, 20, 30]}
        component="div"
        count={tableData.length}
        rowsPerPage={rowsPerPage}
        page={(page > (tableData.length/rowsPerPage)) ? 0 : page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        showFirstButton
        showLastButton
      />

      {dialogueData && 
        <Dialog
          open={open}
          TransitionComponent={Transition}
          keepMounted
          onClose={handleClose}
          fullWidth={true}
          maxWidth={maxWidth}
          scroll="paper"
          aria-describedby="alert-dialog-slide-description"
          PaperProps={{
            style: {
              backgroundColor: 'var(--new-bg-color)',
              borderRadius: '10px'
            }
          }}
        >
          {dialogueData.dialogue === "report" && 
            <StudentReport handleClose={handleClose} testId={dialogueData.testId} studId={dialogueData.studId} lang={dialogueData.lang} allLangs={dialogueData.allLangs} thresholds={dialogueData.thresholds}/>
          }
          {dialogueData.dialogue === "demo-report" && 
            <StudentReportDemo handleClose={handleClose} testId={dialogueData.testId} studId={dialogueData.studId} lang={dialogueData.lang} allLangs={dialogueData.allLangs} thresholds={dialogueData.thresholds}/>
          }
          {dialogueData.dialogue === "Edit Details" &&
            <EditStudent handleClose={handleClose} studentData={dialogueData.data}/>
          }
          {dialogueData.dialogue === "Delete Student" &&
            <DeleteStudent handleClose={handleClose} studentData={dialogueData.data}/>
          }
          {dialogueData.dialogue === "Transfer to Dropbox" &&
            <TransferStudent handleClose={handleClose} studentData={dialogueData.data} status={0}/>
          }
          {dialogueData.dialogue === "Mark Long Absentee" &&
            <TransferStudent handleClose={handleClose} studentData={dialogueData.data} status={1}/>
          }
        </Dialog>
      }
      { optionsData &&
        <Menu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={anchorEl}
        open={openOptions}
        onClose={handleCloseOptions}
        slotProps={{
          paper: {
            style: {
              // maxHeight: ITEM_HEIGHT * 4.5,
              // width: '10ch',
            },
          },
        }}
      >
        {optionsData.options.map((option, i) => (
          <MenuItem key={option} onClick={() => {
              optionsData.data.handleClick ? 
                optionsData.data.handleClick(option, (({ handleClick, ...rest }) => rest)(optionsData.data))
              : 
                handleClickOpen({"dialogue": option, "data": optionsData.data}) 
              handleCloseOptions()
            }}
          >
            {optionsData.icons[i]}
            {option}
          </MenuItem>
        ))}
      </Menu>
      }
    </>
  );
};

export default TableComponent;
